import React, { useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import * as Sentry from '@sentry/react';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSelectField, { ListType } from '../../uiParts/form/CustomSelectField';
import CustomRadioButton from '../../uiParts/form/CustomRadioButton';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { usePatternList } from '../../../hooks/usePatternList';
import { issueEmailInbox } from '../../../api/firebase/function/emailInboxFunction';
import { isValidSender, parseSenders } from '../../../utils/emailSender';
import {
  emailInboxType,
  fileProcessingType,
  EMAIL_INBOX_NAME_MAX_LENGTH,
} from '../../../constants/enum';
import {
  gray20,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  gray5,
  red50,
} from '../../../constants/colors';
import { boldMdGray, regularSm } from '../../../constants/location';
import { HELP_FAQ_UPLOAD_FILE_SPEC } from '../../../constants/externalUrls';
import { EMAIL_ATTACHMENT_HELP_LABEL } from '../../../constants/labels';

const styles = {
  intro: {
    backgroundColor: gray5,
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '24px',
  },
  introText: {
    ...regularSm,
    color: gray100,
  },
  introSteps: {
    ...regularSm,
    color: gray80,
    marginTop: '8px',
  },
  // 添付ファイルの制限ヘルプリンク（ホーム画面「自動仕分とは」と同じ体裁）
  helpLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '12px',
    fontSize: '14px',
    fontWeight: 700,
    color: primaryButtonBackgroundColor.backgroundColor,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sectionTitle: {
    ...boldMdGray,
    marginBottom: '16px',
  },
  buttonLarge: {
    height: '44px',
    padding: '0 16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  // ダイアログ内で本文だけスクロールさせ、発行ボタンを下部固定フッターにするための土台
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
  },
  // スクロールする本文領域。スクロールバーを Paper の右パディング寄りに出す（負 margin で相殺）
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    paddingRight: '24px',
    marginRight: '-24px',
  },
  // 発行ボタンの固定フッター。Paper の横パディング（32px/モバイル16px）分だけ左右いっぱいに罫線を引く
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    borderTop: `1px solid ${gray20}`,
    margin: '0 -32px',
    padding: '16px 32px 0',
    '@media screen and (max-width:960px)': {
      margin: '0 -16px',
      padding: '16px 16px 0',
    },
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    marginBottom: '20px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  // 許可送信元フィールドの補足（空欄時の挙動を説明）
  hintText: {
    ...regularSm,
    color: gray80,
    marginTop: '4px',
  },
  // CustomRadioButton は root の marginBottom(24px)＋空ヘルパーで余白が広くなるため、
  // 空ヘルパーを隠し margin を相殺して他フィールド（gap:16px）と同じ間隔に揃える。
  radioWrapper: {
    marginBottom: '-24px',
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
};

interface Props {
  /**
   * 操作結果の通知。スナックバーはダイアログの外（ページ側）で表示するため親に委譲する。
   * （Dialog 内で position:fixed のスナックバーを描画するとサイズ・位置が崩れるため）
   */
  onNotify: (message: string, isError?: boolean) => void;
  /** 発行成功時のコールバック（親側で一覧画面への遷移や再読み込みを行う） */
  onIssued: () => void;
}

/**
 * メール取り込みの専用アドレス発行フォーム。
 * アドレス設定ダイアログ内に表示し、発行後は親（onIssued）が一覧画面へ遷移・再読み込みする。
 */
const EmailInboxManagement: React.FC<Props> = ({ onNotify, onIssued }) => {
  const { companyId } = useCompanyId();
  const { patterns } = usePatternList();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // 発行フォーム: 「適用する読取パターン」1つで種別とパターンを兼ねる。
  // 値が company_common のとき自動仕分け、パターンIDのとき直接適用。
  const [selectedPattern, setSelectedPattern] = useState<string>(emailInboxType.companyCommon);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [allowedSendersText, setAllowedSendersText] = useState('');
  const [allowlistError, setAllowlistError] = useState('');
  // 出力方式（自動仕分のときのみ適用。パターン指定時はパターンの設定を使う）
  const [outputType, setOutputType] = useState<number>(fileProcessingType.pagePerResult);

  // 自動仕分（company_common）かどうか。出力方式セレクタの表示・送信可否に使う。
  const isCommon = selectedPattern === emailInboxType.companyCommon;

  // 「適用する読取パターン」の選択肢: 先頭に「自動仕分」、その後に各読取パターン
  const patternOptions: ListType[] = [
    { value: emailInboxType.companyCommon, option: '自動仕分（AIが書類を自動で仕分けます）' },
    ...patterns.map(p => ({ value: p.patternId, option: p.pattern_name })),
  ];

  const handleIssue = async (): Promise<void> => {
    if (!companyId) return;
    // 送信時バリデーション
    let hasError = false;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('名称を入力してください。');
      hasError = true;
    } else if (trimmedName.length > EMAIL_INBOX_NAME_MAX_LENGTH) {
      setNameError(`名称は${EMAIL_INBOX_NAME_MAX_LENGTH}文字以内で入力してください。`);
      hasError = true;
    }
    // allowlist は任意入力。入力がある場合のみ形式チェックする（空欄は全送信元許可を意味する）
    const senders = parseSenders(allowedSendersText);
    if (senders.length > 0 && senders.some(s => !isValidSender(s))) {
      setAllowlistError(
        'メールアドレスまたはドメインの形式で入力してください（例: tanaka@example.co.jp / example.co.jp）',
      );
      hasError = true;
    }
    if (hasError) return;
    setSubmitting(true);
    setError('');
    try {
      await issueEmailInbox({
        company_id: companyId,
        inbox_type: isCommon ? emailInboxType.companyCommon : emailInboxType.pattern,
        pattern_id: isCommon ? null : selectedPattern,
        name: trimmedName,
        allowed_senders: senders,
        // 出力方式は inbox に保持し、受信メールのOCR登録時に適用する（自動仕分・パターン指定とも）
        file_type: outputType,
      });
      setName('');
      setSelectedPattern(emailInboxType.companyCommon);
      setAllowedSendersText('');
      setOutputType(fileProcessingType.pagePerResult);
      onNotify('専用アドレスを発行しました。');
      onIssued();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : '発行に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.scrollArea}>
        {/* この画面の説明（初めて使う人向け） */}
        <Box sx={styles.intro}>
          <Typography sx={styles.introText}>
            メールの添付ファイルを自動 OCR するための専用アドレスを発行します。
          </Typography>
          <Typography sx={styles.introSteps}>
            ① アドレスを発行 → ② そのアドレス宛にメールを送る → ③ 添付が自動で取り込まれます。
          </Typography>
          <Link
            component="button"
            type="button"
            onClick={(): void => {
              window.open(HELP_FAQ_UPLOAD_FILE_SPEC, '_blank', 'noopener,noreferrer');
            }}
            sx={styles.helpLink}
          >
            <HelpOutlineOutlined sx={{ fontSize: '16px' }} />
            {EMAIL_ATTACHMENT_HELP_LABEL}
          </Link>
        </Box>

        {error && <Typography sx={styles.errorText}>{error}</Typography>}

        <Typography sx={styles.sectionTitle}>専用アドレスを発行</Typography>
        <Box sx={styles.fieldWrapper}>
          <CustomTextfield
            labelText="名称（一覧で識別するための名称）"
            requiredText="必須"
            value={name}
            onChange={(e): void => {
              setName(e.target.value);
              if (nameError) setNameError('');
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            placeholder="A社 請求書 など"
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
          />
          <CustomSelectField
            labelText="適用する読取パターン"
            list={patternOptions}
            value={selectedPattern}
            onChange={(e): void => setSelectedPattern(e.target.value as string)}
            formMb="0"
          />
          {/* 出力方式は自動仕分・パターン指定とも選択可（OCRのファイルアップロードと同様）。
            ファイルアップロードダイアログ（/ai-ocr/pattern/:id）と同じ CustomRadioButton を使用。 */}
          <Box sx={styles.radioWrapper}>
            <CustomRadioButton
              value={String(outputType)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                setOutputType(Number(event.target.value));
              }}
              labelText="ファイル処理方式"
              required={false}
              firstOption="1ページごとに出力"
              secondOption="複数ページをまとめて出力"
              firstOptionDescription="ページごとに分割してOCR処理"
              secondOptionDescription="複数ページを統合してOCR処理"
              firstValue={fileProcessingType.pagePerResult}
              secondValue={fileProcessingType.singleResult}
            />
          </Box>
          <Box>
            <CustomTextfield
              labelText="許可送信元（allowlist・1行に1件。メールアドレス または ドメイン）"
              value={allowedSendersText}
              onChange={(e): void => {
                setAllowedSendersText(e.target.value);
                if (allowlistError) setAllowlistError('');
              }}
              error={Boolean(allowlistError)}
              helperText={allowlistError}
              placeholder={'tanaka@example.co.jp\nexample.co.jp'}
              multiline
              minRows={2}
              inputWidth="100%"
              fontSizeIs14px
              formMb="0"
            />
            <Typography sx={styles.hintText}>
              空欄の場合、すべての送信元からのメールを受け付けます。特定の相手だけに限定したい場合は入力してください。
              ※なりすましなど不正なメールは自動的に除外されます。
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.footer}>
        <Button
          onClick={(): void => void handleIssue()}
          disabled={submitting}
          startIcon={<AddIcon sx={{ height: '18px', width: '18px' }} />}
          sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
        >
          発行する
        </Button>
      </Box>
    </Box>
  );
};

export default EmailInboxManagement;
